<template>
  <main class="login-page">
    <button class="back-home" type="button" @click="router.push('/portal')">
      <Icon icon="lucide:arrow-left" :size="18" />
      返回首页
    </button>

    <section class="login-shell">
      <aside class="brand-panel">
        <div>
          <div class="brand">
            <img :src="loginLogo" :alt="brandName" />
            <span class="sr-only">{{ brandName }}</span>
          </div>
          <h1>企业AI数字化平台</h1>
          <p>一个账号，统一访问企业应用、业务数据与协作服务。</p>
        </div>
        <div class="brand-points">
          <span><Icon icon="lucide:check" :size="16" />统一工作台</span>
          <span><Icon icon="lucide:check" :size="16" />安全身份认证</span>
          <span><Icon icon="lucide:check" :size="16" />多端数据同步</span>
        </div>
      </aside>

      <div class="form-panel">
        <div v-if="showAccountPanel" class="account-panel">
          <div class="account-avatar">
            <img
              v-if="showAvatarImage"
              :src="currentUser.avatar"
              alt=""
              @error="handleAvatarError"
            />
            <img v-else :src="defaultAvatar" alt="" />
          </div>
          <p class="account-label">当前登录账号</p>
          <button class="account-name" type="button" @click="enterAdmin">
            {{ currentUser.nickname || '已登录用户' }}
          </button>
          <p class="account-tip">当前账号已登录，无需再次输入账号密码。</p>
          <button class="enter-button" type="button" @click="enterAdmin" :disabled="!panelReady">
            <span v-if="!panelReady" class="btn-spinner" />
            {{ panelReady ? '进入管理平台' : '正在准备...' }}
            <Icon v-if="panelReady" icon="lucide:arrow-right" :size="17" />
          </button>
        </div>

        <template v-else>
          <LoginForm />
          <MobileForm />
          <QrCodeForm />
          <ForgetPasswordForm />
          <RegisterForm />
          <SSOLoginVue />
        </template>
      </div>
    </section>
  </main>
</template>

<script lang="ts" setup>
import { Icon } from '@/components/Icon'
import { useRoute, useRouter } from 'vue-router'
import { getAdminAccessToken } from '@/utils/auth'
import { useUserStore } from '@/store/modules/user'
import {
  ForgetPasswordForm,
  LoginForm,
  MobileForm,
  QrCodeForm,
  RegisterForm,
  SSOLoginVue
} from './components'
import { LoginStateEnum, useLoginState } from './components/useLogin'
import { useSystemBrand } from '@/hooks/web/useSystemBrand'
import { parseRouteLocation } from '@/utils/routeParams'
import defaultAvatar from '@/assets/imgs/avatar.jpg'

defineOptions({ name: 'PortalLoginPage' })

const router = useRouter()
const route = useRoute()
const userStore = useUserStore()
const { setLoginState } = useLoginState()
const { brandName, loginLogo, loadSystemBrand } = useSystemBrand()

const currentUser = computed(() => userStore.getUser)

// 头像加载失败时降级为默认图标，避免头像地址不可达时显示裂图
const avatarLoadError = ref(false)
const showAvatarImage = computed(
  () => Boolean(currentUser.value.avatar) && !avatarLoadError.value
)
const handleAvatarError = () => {
  avatarLoadError.value = true
}
watch(
  () => currentUser.value.avatar,
  () => {
    avatarLoadError.value = false
  }
)

// 从门户导航栏点"登录"进入（登录状态下） → 显示"进入管理平台"
// 直接访问 /portal/login（未登录或首次登录成功） → 自动跳转
const fromPortal = computed(() => route.query.from === 'portal')
const showAccountPanel = ref(false)
const panelReady = ref(false)

function resolvePostLoginTarget() {
  const raw = typeof route.query.redirect === 'string' ? route.query.redirect : ''
  if (raw) return parseRouteLocation(raw)
  return { path: '/home' }
}

function enterAdmin() {
  router.push(resolvePostLoginTarget())
}

onMounted(async () => {
  await loadSystemBrand()
  const hasToken = Boolean(getAdminAccessToken())

  if (hasToken) {
    // 从门户点"登录"进入 → 显示"进入管理平台"按钮
    if (fromPortal.value) {
      showAccountPanel.value = true
      try {
        // 登录页只刷新账号信息；动态路由由进入管理平台时的路由守卫统一初始化。
        await userStore.setUserInfoAction()
      } catch {
        // 加载失败不影响展示，路由守卫会兜底
      } finally {
        panelReady.value = true
      }
    } else {
      // 直接访问登录页（含独立窗回跳）→ 自动跳转目标页
      router.replace(resolvePostLoginTarget())
    }
    return
  }

  setLoginState(route.query.mode === 'register' ? LoginStateEnum.REGISTER : LoginStateEnum.LOGIN)
})
</script>

<style lang="scss" scoped>
.login-page {
  min-height: 100vh;
  padding: 32px;
  background: #f5f7fa;
  display: grid;
  place-items: center;
  position: relative;
  overflow-y: auto;
}

.back-home {
  position: absolute;
  top: 28px;
  left: 32px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  background: transparent;
  color: #475569;
  font-size: 14px;
  cursor: pointer;
}

.login-shell {
  width: min(920px, 100%);
  min-height: 580px;
  display: grid;
  grid-template-columns: 42% 58%;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 16px 40px rgb(15 23 42 / 10%);
}

.brand-panel {
  padding: 52px 44px;
  background: #0f3f8c;
  color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.brand {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 21px;
  font-weight: 700;
}

.brand img {
  width: min(220px, 100%);
  height: 54px;
  object-fit: contain;
  object-position: left center;
}

.brand-panel h1 {
  margin: 72px 0 16px;
  font-size: 30px;
  line-height: 1.35;
  letter-spacing: 0;
}

.brand-panel p {
  margin: 0;
  color: rgb(255 255 255 / 76%);
  font-size: 15px;
  line-height: 1.8;
}

.brand-points {
  display: grid;
  gap: 14px;
  color: rgb(255 255 255 / 86%);
  font-size: 14px;
}

.brand-points span {
  display: flex;
  align-items: center;
  gap: 9px;
}

.form-panel {
  padding: 54px 58px 40px;
  align-self: center;
  min-width: 0;
}

.account-panel {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.account-avatar {
  width: 88px;
  height: 88px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  overflow: hidden;
  color: #2563eb;
  background: #eff6ff;
  border: 1px solid #dbeafe;
}

.account-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.account-label {
  margin: 26px 0 8px;
  color: #64748b;
  font-size: 14px;
}

.account-name {
  border: 0;
  padding: 0;
  background: transparent;
  color: #0f172a;
  font-size: 24px;
  font-weight: 700;
  cursor: pointer;
}

.account-name:hover {
  color: #2563eb;
}

.account-tip {
  margin: 14px 0 30px;
  color: #64748b;
  font-size: 14px;
}

.enter-button {
  width: 100%;
  height: 44px;
  border: 0;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #2563eb;
  color: #fff;
  font-size: 15px;
  cursor: pointer;
}

.enter-button:hover:not(:disabled) {
  background: #1d4ed8;
}

.enter-button:disabled {
  opacity: 0.7;
  cursor: wait;
}

.btn-spinner {
  width: 17px;
  height: 17px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: btn-spin 0.6s linear infinite;
}

@keyframes btn-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 720px) {
  .login-page {
    display: block;
    padding: 80px 20px 24px;
  }

  .back-home {
    top: 24px;
    left: 20px;
  }

  .login-shell {
    display: block;
    min-height: auto;
  }

  .brand-panel {
    padding: 24px;
  }

  .brand-panel h1,
  .brand-panel p,
  .brand-points {
    display: none;
  }

  .form-panel {
    padding: 32px 22px 24px;
  }
}
</style>
